import type { RefObject } from "react";
import type { SharedValue } from "@-/com.npmjs.react-native-reanimated";
import { createContext } from "@-/com.npmjs.use-context-selectors";
import type { LearnMapCardFrame } from "#learn-map-cards-motion";

/**
 * How leaving the map home opens the cards, registered by Learn's summary beneath it. Called on
 * the press, it returns what runs once the cards have slid in: it either opens them itself (Start:
 * the deck on the next card) or calls `back` to return to the cards the map lies over.
 */
export type LearnMapHomeExit = () => (back: () => void) => void;

/** Learn's two modes, cards and map, as `LearnMapModeProvider` derives them from the route. */
export type LearnMapMode = {
  /**
   * Card UI has been requested by a press to leave the map or an explicit cards route. False on
   * a map launch: the deck, summary and card chrome must not mount before that request.
   * Retained after the first request so an active deck keeps its session and Undo history.
   */
  areCardsRequested: boolean;
  /**
   * Where Learn's card rests, measured by the deck; `null` until it has been. The chrome the card
   * slides over on its way to the map times its fade by it.
   */
  cardFrame: SharedValue<LearnMapCardFrame | null>;
  /**
   * Learn's route beneath the map: the live pathname while no map route is open, and the last one
   * otherwise. The cards keep showing it while they slide away and back.
   */
  cardsPathname: string;
  /**
   * The map is Learn's home: a map route before the learner has gone to the cards in this app
   * session. A cold launch opens Learn on it. Its sheet greets the learner (Welcome Back) with the
   * way into the cards, and its top-left control is Learn rather than Back. A new learner's
   * onboarding, which tours the map itself, is never the home.
   */
  isMapHome: boolean;
  /**
   * The map's top-left control is the home's (Learn, not Back): `isMapHome`, from the press that
   * slides the cards away to the home (`isMapToolbarCurrent`) until the cards are back.
   */
  isMapHomeControl: boolean;
  /** A map route is Learn's route: the map, or a page opened from it. */
  isMapOpen: boolean;
  /**
   * The map's content is mounted beneath the cards, ready to slide in: once Learn is idle, or the
   * moment the map is first opened. It then stays mounted, as the Map tab once did.
   */
  isMapPrepared: boolean;
  /** The map is on screen or on its way in or out; it draws only then. */
  isMapPresent: boolean;
  /** The map has been opened at least once, so its download may start. */
  isMapRequested: boolean;
  /** The map itself is Learn's route, not a page opened over it. */
  isMapShown: boolean;
  /**
   * Whether the map's floating toolbar, not Learn's, is the one on screen. It switches on the press
   * that opens or closes the map, so the shell's status band follows the slide, not the route.
   */
  isMapToolbarCurrent: boolean;
  /**
   * The home's way into the cards (`LearnMapHomeExit`), set by Learn's summary while the map is the
   * home; `useLeaveLearnMap` runs it. A ref, so registering it re-renders nothing.
   */
  mapHomeExitRef: RefObject<LearnMapHomeExit | null>;
  /** 0 with the cards on screen, 1 with the map; eased on every change of mode. */
  progress: SharedValue<number>;
  /**
   * The top edge of the map's sheet in window coordinates, as it slides in and out with the map
   * or is dragged; `Infinity` while the map shows no sheet. The cards' bottom controls stay put
   * and are hidden beneath it as it slides over them.
   */
  sheetTop: SharedValue<number>;
  /** Actual, unclamped sheet edge for decorative content attached to its grip. */
  perchSheetTop?: SharedValue<number>;
  /**
   * Opens (`true`) or closes the map from a press: the slide starts at once, on the UI thread, and
   * `navigate` (the route change) runs once it has finished, so the route's commit never lands in
   * the middle of the slide. A slide whose route never follows is taken back after a deadline.
   */
  switchMode: (isMapOpen: boolean, navigate: () => void) => void;
  /**
   * 1 while the map's own toolbar replaces Learn's. It follows `progress` when the map opens or
   * closes and snaps when a page opens over the map (those pushes are not animated).
   */
  toolbarProgress: SharedValue<number>;
};

/**
 * Provided by `LearnLayout`; absent (`null`) when a test renders a Learn surface on its own. A
 * selector context: the deck, the summary and the toolbars each re-render only for what they read,
 * so the press that starts a slide does not re-render the deck in the middle of it.
 */
export const LearnMapModeContext = createContext<LearnMapMode | null>(null);
